import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import type { CountResult } from '../../types/analytics.types';
import { formatNumber, formatCompactNumber } from '../../lib/formatters';
import { cn } from '@/lib/utils';

interface BigNumberChartProps {
  data: CountResult;
  title?: string;
}

export function BigNumberChart({ data, title }: BigNumberChartProps) {
  const { count, comparisonCount, comparisonLabel, subtitle } = data;

  const showComparison = comparisonCount !== undefined;
  const change = showComparison ? count - comparisonCount : 0;
  const changePercent = showComparison && comparisonCount > 0
    ? ((change / comparisonCount) * 100).toFixed(1)
    : '0';

  const isPositive = change > 0;
  const isNegative = change < 0;
  const isNeutral = change === 0;

  return (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      {title && (
        <p className="text-sm text-muted-foreground mb-2">{title}</p>
      )}

      <div className="text-4xl md:text-5xl font-bold text-foreground tabular-nums break-all">
        {count >= 10000 ? formatCompactNumber(count) : formatNumber(count)}
      </div>

      <div className="text-lg text-muted-foreground mt-1">
        {subtitle || 'patients'}
      </div>

      {showComparison && (
        <div className="flex items-center gap-2 mt-4">
          <div
            className={cn(
              'flex items-center gap-1 text-sm font-medium',
              isPositive && 'text-success',
              isNegative && 'text-destructive',
              isNeutral && 'text-muted-foreground'
            )}
          >
            {isPositive && <ArrowUp className="h-4 w-4" />}
            {isNegative && <ArrowDown className="h-4 w-4" />}
            {isNeutral && <Minus className="h-4 w-4" />}
            <span>
              {isPositive && '+'}
              {changePercent}%
            </span>
          </div>

          {comparisonLabel && (
            <span className="text-sm text-muted-foreground">
              {comparisonLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
